import { useState, useMemo, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Database, Upload, LoaderCircle, Table2, ArrowRight, Wand2, X, Check, TriangleAlert, Info, Link2, FileX,
  UtensilsCrossed, Wheat, Truck, ShieldCheck, MousePointerClick, Eye, CircleCheckBig, ArrowLeft, ListChecks,
} from 'lucide-react';
import api from '../../api/axios';
import SearchSelect from '../../components/SearchSelect';
import { confirm as confirmModal } from '../../components/ConfirmDialog';
import { chunkedImport } from '../../utils/chunkImport';
import { parseSqlDump } from '../../utils/sqlDump';

// ── İdxal / İxrac v2 ─────────────────────────────────────────────────────────
// Köhnə proqramın SQLite (.db) və ya SQL dump (.sql) bazası BRAUZERDƏ oxunur (serverə yüklənmir).
// Superadmin solda mənbə cədvəli, sağda FiscalPOS hədəfini seçir və sütunları uyğunlaşdırır.
// Göndəriş /superadmin/tenants/:id/import-v2/:hədəf ilə gedir: tenant_id, kateqoriya yaratma,
// təkrar yoxlaması serverdədir; hər hissə bir transaction-dadır (FiscalPOS-dan köçürülüb).

// FiscalPOS-un qəbul etdiyi dəyərlər
const SALE_OPTS = [{ v: 'piece', t: 'ədəd' }, { v: 'weight', t: 'çəki (kq)' }, { v: 'liter', t: 'litr' }];
const ING_UNIT_OPTS = [{ v: 'kg', t: 'kq' }, { v: 'gram', t: 'qram' }, { v: 'litr', t: 'litr' }, { v: 'ml', t: 'ml' }, { v: 'eded', t: 'ədəd' }];
// Köhnə bazadakı mətn → FiscalPOS dəyəri (təxmin; istifadəçi dəyişə bilər)
function guessSale(raw) {
  const u = norm(raw);
  if (/^(kg|kq|kilo|ceki|weight|qram|gram|gr)/.test(u)) return 'weight';
  if (/^(l|lt|litr|litre|liter|ml)$/.test(u)) return 'liter';
  return 'piece';
}
function guessIngUnit(raw) {
  const u = norm(raw);
  if (!u) return 'kg';
  if (/^(gram|qram|gr|g|qr)$/.test(u)) return 'gram';
  if (/^(ml|millilitr|mililitr)$/.test(u)) return 'ml';
  if (/^(l|lt|litr|litre|liter)$/.test(u)) return 'litr';
  if (/^(eded|ed|ede|piece|pcs|reqem|say)/.test(u)) return 'eded';
  return 'kg';
}

// Hədəflər: yalnız istifadəçinin yaza biləcəyi sahələr (id, tenant_id və s. avtomatik)
// opts olan sahələrdə köhnə dəyərlər FiscalPOS dəyərlərinə ayrıca uyğunlaşdırılır
const TARGETS = [
  {
    key: 'menu', label: 'Menyu', hint: 'Kateqoriya və məhsullar', endpoint: 'menu',
    note: 'Eyni kateqoriyada eyni adlı məhsul varsa qiyməti, satış növü və təsviri yenilənir, yoxdursa yeni yaradılır. Kateqoriya yoxdursa avtomatik yaradılır, boşdursa «Ümumi» yazılır.',
    fields: [
      { f: 'ad', l: 'Məhsul adı', req: true, al: ['ad', 'adi', 'name', 'mehsul', 'mehsuladi', 'malinadi', 'title', 'yemek'] },
      { f: 'kateqoriya', l: 'Kateqoriya', al: ['kateqoriya', 'kategoriya', 'kateqoriyaadi', 'qrup', 'qrupu', 'category', 'categoryname', 'catname'] },
      { f: 'qiymet', l: 'Qiymət', al: ['qiymet', 'satisqiymeti', 'satis', 'price', 'qiymeti'] },
      { f: 'satis_novu', l: 'Satış növü', al: ['satisnovu', 'saletype', 'vahid', 'unit', 'olcu'], opts: SALE_OPTS, def: 'piece', guess: guessSale },
      { f: 'tesvir', l: 'Təsvir', al: ['tesvir', 'aciqlama', 'description', 'qeyd', 'tərkib', 'terkib'] },
    ],
  },
  {
    key: 'ingredients', label: 'İnqredientlər', hint: 'Anbar xammalı', endpoint: 'ingredients',
    note: 'Eyni adlı inqredient varsa qiyməti və min stoku yenilənir (vahid dəyişdirilmir, texkartalar pozulmasın), yoxdursa yeni yaradılır. Qalıq «keçən sayım» kimi yazılır.',
    fields: [
      { f: 'ad', l: 'Ad', req: true, al: ['ad', 'adi', 'name', 'inqredient', 'mehsul', 'malinadi'] },
      { f: 'vahid', l: 'Vahid', al: ['vahid', 'unit', 'olcu', 'olcuvahidi'], opts: ING_UNIT_OPTS, def: 'kg', guess: guessIngUnit },
      { f: 'qiymet', l: 'Vahid qiyməti', al: ['qiymet', 'alisqiymeti', 'costperunit', 'vahidqiymet', 'cost', 'alis'] },
      { f: 'min_stok', l: 'Min stok', al: ['minstok', 'minstock', 'min', 'minimum'] },
      { f: 'qaliq', l: 'Qalıq (keçən sayım)', al: ['qaliq', 'stok', 'stock', 'miqdar', 'miqdari', 'sayim'] },
    ],
  },
  {
    key: 'suppliers', label: 'Tədarükçülər', hint: 'Malverənlər', endpoint: 'suppliers',
    note: 'Eyni adlı tədarükçü artıq varsa keçilir (təkrar yaranmır).',
    fields: [
      { f: 'ad', l: 'Ad', req: true, al: ['ad', 'adi', 'name', 'tedarukcu', 'malveren', 'malverenler', 'firma'] },
      { f: 'telefon', l: 'Telefon', al: ['telefon', 'phone', 'nomre', 'mobil', 'tel'] },
      { f: 'qeyd', l: 'Qeyd', al: ['qeyd', 'note', 'unvan', 'address'] },
    ],
  },
];

// "MalınKodu" → "malinkodu", "AlışQiymətiAzn" → "alisqiymetiazn"
const norm = s => String(s || '').toLowerCase()
  .replace(/ı/g, 'i').replace(/ə/g, 'e').replace(/ş/g, 's').replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ö/g, 'o').replace(/ü/g, 'u')
  .replace(/[^a-z0-9]/g, '');

const KOD_FIELD = {}; // FiscalPOS hədəflərində kod sahəsi yoxdur («Boş qalsın» seçimi çıxmır)
const EMPTY = '__EMPTY__';   // «Boş qalsın»: ad boş yazılır (yalnız kodu dolu mallar üçün)
// Məcburi sahə (Ad) boşdursa ehtiyat sütun: defolt Kod (köhnə bazada adı boş, kodu dolu mallar çoxdur)
function autoFallback(target, m) {
  const k = KOD_FIELD[target.key];
  return k && m[k] ? m[k] : '';
}

function autoMap(target, columns) {
  const out = {};
  const used = new Set();
  for (const fd of target.fields) {
    // Ləqəblər prioritet sırası ilə (məs. Tarix2 saatlıdır, Tarix-dən öndə)
    let hit = null;
    for (const a of fd.al) { hit = columns.find(c => !used.has(c) && norm(c) === a); if (hit) break; }
    if (hit) { out[fd.f] = hit; used.add(hit); }
  }
  return out;
}

const cellStr = v => (v == null ? '' : typeof v === 'number' ? String(v) : String(v));

// sql.js (SQLite WASM) — yalnız .db seçiləndə public/vendor/sqljs-dən yüklənir, əsas paketi böyütmür
let sqlPromise = null;
function loadSql() {
  if (!sqlPromise) {
    const base = `${process.env.PUBLIC_URL || ''}/vendor/sqljs`;
    sqlPromise = new Promise((resolve, reject) => {
      if (window.initSqlJs) { resolve(window.initSqlJs); return; }
      const el = document.createElement('script');
      el.src = `${base}/sql-wasm-browser.js`;
      el.onload = () => (window.initSqlJs ? resolve(window.initSqlJs) : reject(new Error('sql.js yüklənmədi')));
      el.onerror = () => reject(new Error('sql.js yüklənmədi'));
      document.head.appendChild(el);
    }).then(init => init({ locateFile: () => `${base}/sql-wasm-browser.wasm` }))
      .catch(e => { sqlPromise = null; throw e; });
  }
  return sqlPromise;
}

const CARD = 'rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]';

export default function ImportV2({ tenants = [] }) {
  const [tenantId, setTenantId] = useState('');
  const [files,    setFiles]    = useState([]);     // [{ name, tables: [{ key, table, columns, rows }] }]
  const [loading,  setLoading]  = useState(false);
  const [srcKey,   setSrcKey]   = useState('');
  const [tgtKey,   setTgtKey]   = useState('');
  const [map,      setMap]      = useState({});     // { hədəfSahə: mənbəSütun }
  const [pick,     setPick]     = useState(null);   // klikləmə ilə cütləmə: seçilmiş mənbə sütunu
  const [fallback, setFallback] = useState('');     // məcburi sahə boşdursa bu sütundan götür
  const [busy,     setBusy]     = useState(false);
  const [prog,     setProg]     = useState(null);
  const [result,   setResult]   = useState(null);
  const [over,     setOver]     = useState(false);
  const [valMap,   setValMap]   = useState({});     // { sahə: { köhnəDəyər: fiscalDəyər } }
  const [constVal, setConstVal] = useState({});     // { sahə: dəyər } sütun seçilməyibsə hamısı üçün
  const [page,     setPage]     = useState(1);      // 1 fayl · 2 cədvəl · 3 sütun · 4 import
  const fileRef = useRef(null);

  const tenant = tenants.find(t => String(t.id) === String(tenantId));
  const tenantLabel = t => (t.name || t.username || `Tenant ${t.id}`);
  const allTables = useMemo(() => files.flatMap(f => f.tables.map(t => ({ ...t, file: f.name }))), [files]);
  const src = allTables.find(t => t.key === srcKey) || null;
  const tgt = TARGETS.find(t => t.key === tgtKey) || null;
  const tgtDisabled = () => false;

  async function onFiles(list) {
    const arr = Array.from(list || []).filter(Boolean);
    if (!arr.length) return;
    setLoading(true);
    try {
      const added = [];
      for (const file of arr) {
        try {
          // .sql dump (mysqldump / phpMyAdmin / SQLite .dump / SQL Server): mətn kimi oxu
          if (/\.sql$/i.test(file.name)) {
            const buf = await file.arrayBuffer();
            if (!buf.byteLength) { toast.error(`${file.name}: fayl boşdur (0 bayt)`); continue; }
            let text = new TextDecoder('utf-8').decode(buf);
            if (text.includes('�')) text = new TextDecoder('windows-1254').decode(buf); // köhnə Windows kodlaşması
            const parsed = parseSqlDump(text);
            if (!parsed.length) { toast.error(`${file.name}: içində CREATE TABLE / INSERT INTO tapılmadı`); continue; }
            added.push({ name: file.name, tables: parsed.map(t => ({ key: `${file.name}::${t.table}`, ...t })) });
            continue;
          }
          const SQL = await loadSql();
          const db = new SQL.Database(new Uint8Array(await file.arrayBuffer()));
          const names = db.exec("SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%' ORDER BY name");
          const tables = [];
          for (const [tname] of (names[0]?.values || [])) {
            const q = db.exec(`SELECT * FROM "${String(tname).replace(/"/g, '""')}"`);
            let columns = q[0]?.columns;
            if (!columns) {   // boş cədvəl: sütunları sxemdən götür
              const info = db.exec(`PRAGMA table_info("${String(tname).replace(/"/g, '""')}")`);
              columns = (info[0]?.values || []).map(r => r[1]);
            }
            const rows = (q[0]?.values || []).map(v => Object.fromEntries(columns.map((c, i) => [c, v[i]])));
            tables.push({ key: `${file.name}::${tname}`, table: tname, columns, rows });
          }
          db.close();
          added.push({ name: file.name, tables });
        } catch (e) {
          console.error(e);
          toast.error(`${file.name}: baza faylı oxunmadı`);
        }
      }
      if (added.length) {
        setFiles(prev => [...prev.filter(p => !added.some(a => a.name === p.name)), ...added]);
        const n = added.reduce((s, f) => s + f.tables.length, 0);
        toast.success(`${added.length} fayl, ${n} cədvəl oxundu`);
      }
    } catch (e) {
      console.error(e);
      toast.error('SQLite oxuyucu yüklənmədi');
    } finally { setLoading(false); }
  }

  function chooseSrc(key) {
    setSrcKey(key); setResult(null); setPick(null); setValMap({});
    const s = allTables.find(t => t.key === key);
    applyAuto(tgt, s);
  }
  function chooseTgt(key) {
    setTgtKey(key); setResult(null); setPick(null);
    const t = TARGETS.find(x => x.key === key);
    applyAuto(t, src);
  }
  function applyAuto(t, s) {
    if (s && t) { const m = autoMap(t, s.columns); setMap(m); setFallback(autoFallback(t, m)); }
    else { setMap({}); setFallback(''); }
  }
  function removeFile(name) {
    setFiles(prev => prev.filter(f => f.name !== name));
    if (src && src.file === name) { setSrcKey(''); setMap({}); }
  }
  function link(field, col) {
    setMap(m => {
      const next = { ...m };
      for (const k of Object.keys(next)) if (next[k] === col) delete next[k]; // bir sütun bir sahəyə
      if (col) next[field] = col; else delete next[field];
      return next;
    });
    setPick(null);
  }

  // Uyğunlaşdırılmış sətirlər (hədəf sahə adları ilə)
  const mapped = useMemo(() => {
    if (!src || !tgt) return [];
    return src.rows.map((r, idx) => {
      const o = { _i: idx };
      for (const fd of tgt.fields) {
        const c = map[fd.f];
        if (!c) {
          if (fd.opts) o[fd.f] = constVal[fd.f] || fd.def;   // sütunsuz: seçilən (defolt ədəd) hamısına
          continue;
        }
        const v = r[c];
        if (fd.opts) {
          const k = cellStr(v).trim();
          o[fd.f] = (valMap[fd.f] && valMap[fd.f][k]) || fd.guess(k);
          continue;
        }
        o[fd.f] = typeof v === 'string' ? v.trim() : v;
      }
      const rq = tgt.fields.find(f => f.req);
      if (rq && fallback && fallback !== EMPTY && !cellStr(o[rq.f]).trim()) o[rq.f] = cellStr(r[fallback]).trim();
      return o;
    });
  }, [src, tgt, map, fallback, valMap, constVal]);

  // opts sahələri üçün köhnə bazadakı fərqli dəyərlər (say ilə)
  const enumInfo = useMemo(() => {
    if (!src || !tgt) return [];
    return tgt.fields.filter(fd => fd.opts).map(fd => {
      const col = map[fd.f];
      if (!col) return { fd, col: null, values: [] };
      const cnt = new Map();
      for (const r of src.rows) { const k = cellStr(r[col]).trim(); cnt.set(k, (cnt.get(k) || 0) + 1); }
      const values = [...cnt.entries()].sort((a, b) => b[1] - a[1]).map(([v, n]) => ({ v, n }));
      return { fd, col, values };
    });
  }, [src, tgt, map]);
  const reqField = tgt?.fields.find(f => f.req);
  const kodF = tgt ? KOD_FIELD[tgt.key] : null;
  const emptyMode = fallback === EMPTY && !!kodF;
  const isValid = r => !!cellStr(r[reqField.f]).trim() || (emptyMode && !!cellStr(r[kodF]).trim());
  const validCount = reqField ? mapped.filter(isValid).length : 0;
  const ready = !!(tenant && src && tgt && reqField && map[reqField.f] && validCount > 0 && !tgtDisabled(tgt));

  async function doImport() {
    if (!ready) return;
    const rows = mapped.filter(isValid);
    const ok = await confirmModal({
      title: 'İmport',
      message: `${tenantLabel(tenant)} hesabına «${tgt.label}» bölməsinə ${rows.length} sətir import ediləcək (${src.file} › ${src.table}). Davam edilsin?`,
      confirmText: 'Import et',
    });
    if (!ok) return;
    setBusy(true); setResult(null); setProg(0);
    try {
      const payload = rows.map(({ _i, ...rest }) => rest);
      // Server xətası (500) olsa da cavabı oxu: hər hissə öz transaction-ındadır, alınmayan hissə yazılmır
      const post = part => api.post(`/superadmin/tenants/${tenantId}/import-v2/${tgt.endpoint}`, { rows: part })
        .catch(e => ({ data: e?.response?.data || { ok: false, msg: 'Server cavab vermədi' } }));
      const res = await chunkedImport(payload, post, pct => setProg(pct));
      if (!res.ok) { toast.error(res.msg || 'Import alınmadı', { duration: 8000 }); if (res.created || res.updated) setResult(res); return; }
      setResult(res);
      toast.success(`İmport tamamlandı · ${res.created || 0} yeni, ${res.updated || 0} yeniləndi`);
    } catch (e) { console.error(e); toast.error('Import xətası'); }
    finally { setBusy(false); setProg(null); }
  }

  const sample = col => {
    if (!src) return '';
    const r = src.rows.find(x => cellStr(x[col]).trim());
    return r ? cellStr(r[col]).trim() : '';
  };

  // ── Səhifələr (wizard) ──
  const mappedCount = tgt ? tgt.fields.filter(fd => map[fd.f]).length : 0;
  const ok1 = !!(tenant && files.length);
  const ok2 = ok1 && !!(src && tgt && !tgtDisabled(tgt));
  const ok3 = ok2 && !!(reqField && map[reqField.f] && validCount > 0);
  const maxPage = ok3 ? 4 : ok2 ? 3 : ok1 ? 2 : 1;
  const canNext = page === 1 ? ok1 : page === 2 ? ok2 : page === 3 ? ok3 : false;
  const go = p => { if (p >= 1 && p <= maxPage) setPage(p); };
  // Fayl/cədvəl dəyişib addım əlçatmaz olubsa, uyğun səhifəyə qayıt
  useEffect(() => { if (page > maxPage) setPage(maxPage); }, [page, maxPage]);
  const steps = [
    { n: 1, t: 'Fayllar',          d: ok1 ? `${tenantLabel(tenant)} · ${files.length} fayl` : 'Tenant və baza faylı' },
    { n: 2, t: 'Cədvəl və hədəf',  d: src && tgt ? `${src.table} → ${tgt.label}` : 'Haradan hara' },
    { n: 3, t: 'Sütunlar',         d: tgt ? `${mappedCount} / ${tgt.fields.length} sahə` : 'Uyğunlaşdırma' },
    { n: 4, t: 'Import',           d: result ? 'Tamamlandı' : ok3 ? `${validCount} sətir hazır` : 'Yoxla və göndər' },
  ];
  const TICON = { menu: UtensilsCrossed, ingredients: Wheat, suppliers: Truck };
  const hint = page === 1 ? (!tenant ? 'Tenant seçin' : !files.length ? 'Ən azı bir .db və ya .sql faylı əlavə edin' : '')
    : page === 2 ? (!src ? 'Solda köhnə cədvəli seçin' : !tgt ? 'Sağda FiscalPOS bölməsini seçin' : '')
    : page === 3 ? (reqField && !map[reqField.f] ? `«${reqField.l}» sahəsi mütləq uyğunlaşdırılmalıdır` : validCount === 0 ? 'Import olunacaq sətir yoxdur' : '')
    : '';

  const PageHead = ({ title, sub }) => (
    <div className="mb-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Addım {page} / 4</p>
      <h3 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
      {sub && <p className="mt-0.5 text-sm text-slate-500">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-5 pb-24">
      {/* ── Başlıq + addımlar ── */}
      <div className="relative overflow-hidden rounded-2xl bg-[#0b1220] p-5 text-white sm:p-6">
        <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#0090bb]/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-10 h-48 w-48 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-4">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15"><Database className="h-5 w-5 text-sky-300" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold tracking-tight">Köhnə proqramdan FiscalPOS-a köçürmə</p>
            <p className="text-sm text-white/60">SQLite (.db) və ya SQL (.sql) faylını oxu, cədvəli və sütunları özün uyğunlaşdır, bir kliklə import et.</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80 ring-1 ring-white/10">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" /> Fayl serverə yüklənmir
          </span>
        </div>
        <ol className="relative mt-6 grid grid-cols-2 gap-2 lg:grid-cols-4">
          {steps.map(s => {
            const on = page === s.n;
            const done = s.n < page || (s.n === 4 && !!result);
            const reach = s.n <= maxPage;
            return (
              <li key={s.n}>
                <button type="button" disabled={!reach} onClick={() => go(s.n)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left ring-1 transition-colors disabled:cursor-not-allowed ${on ? 'bg-white/[0.1] ring-sky-300/40' : done ? 'bg-emerald-400/10 ring-emerald-400/20 hover:bg-emerald-400/15' : reach ? 'bg-white/[0.04] ring-white/10 hover:bg-white/[0.07]' : 'bg-white/[0.02] ring-white/5'}`}>
                  <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${on ? 'bg-sky-400 text-[#0b1220]' : done ? 'bg-emerald-400 text-[#0b1220]' : 'bg-white/10 text-white/50'}`}>
                    {done && !on ? <Check className="h-3.5 w-3.5" /> : s.n}
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-[13px] font-semibold ${on || done || reach ? 'text-white' : 'text-white/40'}`}>{s.t}</span>
                    <span className="block truncate text-[11px] text-white/45">{s.d}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      {/* ══════ 1. FAYLLAR ══════ */}
      {page === 1 && (
        <section className={`${CARD} p-5 sm:p-7`}>
          <PageHead title="Tenant və baza faylları" sub="Hansı restorana köçürüləcəyini seç və köhnə proqramın .db və ya .sql fayllarını at." />
          <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Hədəf tenant (restoran)</label>
              <SearchSelect value={tenantId} onChange={v => { setTenantId(v); setResult(null); }} placeholder="Tenant seçin"
                options={tenants.map(t => ({ value: String(t.id), label: tenantLabel(t) }))} />
              {tenant && (
                <div className="mt-3 flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0090bb] to-indigo-500 text-xs font-bold text-white">
                    {String(tenantLabel(tenant)).trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-800">{tenantLabel(tenant)}</span>
                    <span className="block truncate font-mono text-[11px] text-slate-400">@{tenant.username} · plan {tenant.plan || 'free'}</span>
                  </span>
                </div>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Köhnə baza faylları</label>
              <div onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
                onDrop={e => { e.preventDefault(); setOver(false); onFiles(e.dataTransfer.files); }}
                className={`group flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all ${over ? 'border-[#0090bb] bg-sky-50' : 'border-slate-200 bg-gradient-to-b from-slate-50/80 to-white hover:border-slate-300'}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[#0090bb] shadow-sm ring-1 ring-slate-200 transition-transform group-hover:-translate-y-0.5">
                  {loading ? <LoaderCircle className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                </span>
                <p className="mt-3 text-sm font-semibold text-slate-700">{loading ? 'Oxunur' : '.db və ya .sql fayllarını buraya at və ya seç'}</p>
                <p className="mt-1 text-xs text-slate-400">SQLite (.db) və ya SQL dump (.sql: mysqldump, phpMyAdmin) · bir neçəsi birlikdə olar</p>
                <input ref={fileRef} type="file" accept=".db,.sqlite,.sqlite3,.sql" multiple className="hidden" onChange={e => { onFiles(e.target.files); e.target.value = ''; }} />
              </div>
              {files.length > 0 && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {files.map(f => (
                    <div key={f.name} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><Database className="h-4 w-4" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-slate-800">{f.name}</span>
                        <span className="block text-[11px] text-slate-400">{f.tables.length} cədvəl · {f.tables.reduce((n, t) => n + t.rows.length, 0)} sətir</span>
                      </span>
                      <button type="button" onClick={() => removeFile(f.name)} title="Çıxar" aria-label="Çıxar"
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══════ 2. CƏDVƏL VƏ HƏDƏF ══════ */}
      {page === 2 && (
        <section className={`${CARD} p-5 sm:p-7`}>
          <PageHead title="Haradan → hara" sub="Solda köhnə bazanın cədvəlini, sağda onun FiscalPOS-da yazılacağı bölməni seç." />
          <div className="grid gap-6 lg:grid-cols-[1fr_auto_1fr]">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">Köhnə baza</span>
                <span className="text-xs font-medium text-slate-500">Mənbə cədvəl</span>
              </div>
              <div className="max-h-[26rem] space-y-1.5 overflow-y-auto pr-1">
                {allTables.map(t => {
                  const on = t.key === srcKey;
                  return (
                    <button key={t.key} type="button" onClick={() => chooseSrc(t.key)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-left transition-all ${on ? 'border-2 border-indigo-500 bg-indigo-50/70' : 'border border-slate-200 bg-white hover:border-slate-300'}`}>
                      <Table2 className={`h-4 w-4 flex-shrink-0 ${on ? 'text-indigo-600' : 'text-slate-400'}`} />
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-sm font-semibold ${on ? 'text-indigo-900' : 'text-slate-800'}`}>{t.table}</span>
                        <span className="block truncate text-[11px] text-slate-400">{t.file} · {t.columns.length} sütun</span>
                      </span>
                      <span className={`rounded-lg px-2 py-0.5 text-xs font-semibold ${t.rows.length ? (on ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600') : 'bg-slate-50 text-slate-300'}`}>{t.rows.length}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="hidden items-center justify-center lg:flex">
              <span className={`flex h-10 w-10 items-center justify-center rounded-full ${src && tgt ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-300'}`}><ArrowRight className="h-5 w-5" /></span>
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded-md bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#0090bb]">FiscalPOS</span>
                <span className="text-xs font-medium text-slate-500">Hədəf bölmə</span>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {TARGETS.map(t => {
                  const on = t.key === tgtKey;
                  const dis = tgtDisabled(t);
                  const TI = TICON[t.key];
                  return (
                    <button key={t.key} type="button" disabled={dis} onClick={() => chooseTgt(t.key)}
                      className={`flex flex-col items-start gap-2 rounded-xl p-3.5 text-left transition-all disabled:cursor-not-allowed disabled:opacity-40 ${on ? 'border-2 border-[#0090bb] bg-sky-50' : 'border border-slate-200 bg-white hover:border-slate-300'}`}>
                      <span className="flex w-full items-center justify-between">
                        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${on ? 'bg-[#0090bb] text-white' : 'bg-slate-100 text-slate-500'}`}><TI className="h-4 w-4" /></span>
                        {on && <Check className="h-4 w-4 text-[#0090bb]" />}
                      </span>
                      <span>
                        <span className={`block text-sm font-semibold ${on ? 'text-[#0090bb]' : 'text-slate-800'}`}>{t.label}</span>
                        <span className="block text-[11px] text-slate-500">{t.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {tgt && (
                <p className="mt-3 flex gap-2 rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />{tgt.note}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══════ 3. SÜTUNLAR ══════ */}
      {page === 3 && src && tgt && (
        <section className={`${CARD} p-5 sm:p-7`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <PageHead title="Sütunları uyğunlaşdır" sub="Solda köhnə sütuna, sonra sağda FiscalPOS sahəsinə kliklə. Və ya sağdakı siyahıdan seç." />
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => { applyAuto(tgt, src); setValMap({}); }}
                className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"><Wand2 className="h-3.5 w-3.5" /> Avtomatik</button>
              <button type="button" onClick={() => { setMap({}); setFallback(''); setPick(null); setValMap({}); setConstVal({}); }}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"><FileX className="h-3.5 w-3.5" /> Təmizlə</button>
            </div>
          </div>

          {pick && (
            <div className="mb-3 flex items-center gap-2 rounded-xl bg-indigo-600 px-3.5 py-2.5 text-xs text-white">
              <MousePointerClick className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1"><b className="font-mono">{pick}</b> seçildi. İndi sağda hansı FiscalPOS sahəsinə yazılacağına kliklə.</span>
              <button type="button" onClick={() => setPick(null)} className="rounded-lg px-2 py-1 font-semibold text-indigo-100 hover:bg-white/10">Ləğv et</button>
            </div>
          )}

          <div className="grid gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            {/* Köhnə sütunlar */}
            <div className="rounded-2xl bg-slate-50/70 p-2.5 ring-1 ring-slate-200/70">
              <div className="flex items-center gap-2 px-1.5 pb-2.5 pt-0.5">
                <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">Köhnə baza</span>
                <span className="truncate text-xs font-semibold text-slate-600">{src.file} › {src.table}</span>
              </div>
              <div className="space-y-1">
                {src.columns.map(c => {
                  const usedBy = tgt.fields.find(fd => map[fd.f] === c);
                  const on = pick === c;
                  return (
                    <button key={c} type="button" onClick={() => setPick(on ? null : c)}
                      className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left transition-all ${on ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/20' : usedBy ? 'bg-white ring-1 ring-emerald-200' : 'bg-white ring-1 ring-slate-200/70 hover:ring-slate-300'}`}>
                      <span className={`min-w-0 flex-1 truncate font-mono text-xs font-semibold ${on ? 'text-white' : 'text-slate-800'}`}>{c}</span>
                      <span className={`max-w-[40%] truncate text-[11px] ${on ? 'text-indigo-100' : 'text-slate-400'}`}>{sample(c)}</span>
                      {usedBy && !on && <span className="flex-shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">→ {usedBy.l}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* FiscalPOS sahələri */}
            <div className="space-y-4">
              <div className="rounded-2xl p-2.5 ring-1 ring-slate-200/70">
                <div className="grid grid-cols-[minmax(0,10rem)_1.25rem_minmax(0,1fr)] items-end gap-2 border-b border-slate-100 px-1.5 pb-2.5 pt-0.5">
                  <span>
                    <span className="block text-[10px] font-semibold uppercase tracking-wider text-[#0090bb]">FiscalPOS</span>
                    <span className="block truncate text-xs font-semibold text-slate-700">{tgt.label}</span>
                  </span>
                  <span />
                  <span>
                    <span className="block text-[10px] font-semibold uppercase tracking-wider text-indigo-700">Köhnə bazadan</span>
                    <span className="block truncate text-xs font-semibold text-slate-700">{src.table} sütunu</span>
                  </span>
                </div>
                <div className="mt-1.5 space-y-1">
                  {tgt.fields.map(fd => {
                    const col = map[fd.f];
                    // Sabit seçimli sahə (vahid): sütun yoxdursa aşağıdakı seçim hamısına yazılır
                    const constLabel = fd.opts && !col ? (fd.opts.find(o => o.v === (constVal[fd.f] || fd.def))?.t || '') : '';
                    return (
                      <div key={fd.f} onClick={() => { if (pick) link(fd.f, pick); }}
                        className={`grid grid-cols-[minmax(0,10rem)_1.25rem_minmax(0,1fr)] items-center gap-2 rounded-xl px-1.5 py-1.5 transition-colors ${pick ? 'cursor-pointer bg-indigo-50/40 ring-1 ring-indigo-200 hover:bg-indigo-50' : ''}`}>
                        <span className="truncate text-[13px] font-medium text-slate-700">{fd.l}{fd.req && <span className="text-red-500"> *</span>}</span>
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full ${col || constLabel ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-300'}`}>
                          {col || constLabel ? <Check className="h-3 w-3" /> : <Link2 className="h-3 w-3" />}
                        </span>
                        <select value={col || ''} onClick={e => e.stopPropagation()} onChange={e => link(fd.f, e.target.value)}
                          className={`min-w-0 w-full rounded-lg border px-2.5 py-1.5 font-mono text-xs focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 ${col ? 'border-emerald-200 bg-emerald-50/40 text-slate-800' : constLabel ? 'border-sky-200 bg-sky-50/50 text-slate-700' : 'border-slate-200 bg-white text-slate-400'}`}>
                          <option value="">{constLabel ? `Sütunsuz · hamısına «${constLabel}»` : 'Seçilməyib'}</option>
                          {src.columns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    );
                  })}
                </div>
                {reqField && (
                  <div className="mt-3 rounded-xl bg-amber-50/70 p-2.5 ring-1 ring-amber-200/60">
                    <div className="grid grid-cols-[minmax(0,10rem)_1.25rem_minmax(0,1fr)] items-center gap-2">
                      <span className="text-[13px] font-medium text-amber-900">{reqField.l} boşdursa</span>
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full ${fallback ? 'bg-amber-200 text-amber-700' : 'bg-amber-100 text-amber-300'}`}><Link2 className="h-3 w-3" /></span>
                      <select value={fallback} onChange={e => setFallback(e.target.value)}
                        className="min-w-0 w-full rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 font-mono text-xs text-slate-700 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100">
                        <option value="">Keçilsin (import olunmasın)</option>
                        {KOD_FIELD[tgt.key] && <option value={EMPTY}>Boş qalsın (ad boş yazılsın)</option>}
                        {src.columns.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <p className="mt-2 text-[11px] leading-relaxed text-amber-800/80">
                      {fallback === EMPTY
                        ? 'Adı boş olan sətir yenə import olunur, adı boş qalır. Kodu da boş olan sətir keçilir.'
                        : fallback
                          ? `Adı boş olan sətrə «${fallback}» sütununun dəyəri ad kimi yazılır.`
                          : 'Adı boş olan sətirlər import olunmur.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Dəyər uyğunlaşdırması (vahid və s.): köhnə mətn → FiscalPOS-un qəbul etdiyi dəyər */}
              {enumInfo.map(({ fd, col, values }) => (
                <div key={fd.f} className="rounded-2xl p-3 ring-1 ring-slate-200/70">
                  <div className="mb-2.5 flex items-start gap-2.5">
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-sky-50 text-[#0090bb]"><ListChecks className="h-4 w-4" /></span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{fd.l} dəyərləri</p>
                      <p className="text-xs text-slate-500">
                        {col
                          ? <>Köhnə bazadakı hər <b className="font-mono">{col}</b> dəyəri FiscalPOS-da nə olsun?</>
                          : `Sütun seçilməyib. Bütün sətirlərə hansı «${fd.l.toLowerCase()}» yazılsın?`}
                      </p>
                    </div>
                  </div>
                  {col ? (
                    <div className="max-h-64 space-y-1 overflow-y-auto">
                      <div className="grid grid-cols-[minmax(0,1fr)_1.25rem_minmax(0,9rem)] gap-2 px-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider">
                        <span className="text-indigo-700">Köhnə bazada</span><span /><span className="text-[#0090bb]">FiscalPOS</span>
                      </div>
                      {values.map(({ v, n }) => {
                        const cur = (valMap[fd.f] && valMap[fd.f][v]) || fd.guess(v);
                        return (
                          <div key={v || '∅'} className="grid grid-cols-[minmax(0,1fr)_1.25rem_minmax(0,9rem)] items-center gap-2 rounded-xl bg-slate-50/70 px-2.5 py-1.5">
                            <span className="flex min-w-0 items-center gap-2">
                              <span className={`truncate font-mono text-xs font-semibold ${v ? 'text-slate-800' : 'italic text-slate-400'}`}>{v || 'boş'}</span>
                              <span className="flex-shrink-0 rounded bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200">{n} sətir</span>
                            </span>
                            <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                            <select value={cur} onChange={e => setValMap(m => ({ ...m, [fd.f]: { ...(m[fd.f] || {}), [v]: e.target.value } }))}
                              className="w-full rounded-lg border border-sky-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100">
                              {fd.opts.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {fd.opts.map(o => {
                        const on = (constVal[fd.f] || fd.def) === o.v;
                        return (
                          <button key={o.v} type="button" onClick={() => setConstVal(c => ({ ...c, [fd.f]: o.v }))}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${on ? 'bg-[#0090bb] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{o.t}</button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════ 4. IMPORT ══════ */}
      {page === 4 && src && tgt && (
        <section className={`${CARD} p-5 sm:p-7`}>
          <PageHead title={result ? 'Import tamamlandı' : 'Yoxla və import et'} sub={result ? `${tenantLabel(tenant)} · ${tgt.label}` : 'Aşağıdakı sətirlər FiscalPOS-a belə yazılacaq. Hər şey düzdürsə «Import et» bas.'} />

          {result ? (
            <div>
              <div className="flex items-center gap-3 rounded-2xl bg-emerald-50/70 p-4 ring-1 ring-emerald-200/60">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-emerald-600 shadow-sm"><CircleCheckBig className="h-6 w-6" /></span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-900">{src.file} › {src.table} köçürüldü</p>
                  <p className="text-xs text-emerald-800/80">Başqa cədvəli köçürmək üçün 2-ci addıma qayıt.</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ['Yeni', result.created || 0, 'text-emerald-600'],
                  ['Yeniləndi', result.updated || 0, 'text-[#0090bb]'],
                  ['Keçildi', result.skipped || 0, 'text-slate-500'],
                  ...(result.categories ? [['Yeni kateqoriya', result.categories, 'text-indigo-600']] : []),
                  ...(result.sayim ? [['Sayım yazıldı', result.sayim, 'text-indigo-600']] : []),
                  ['Göndərildi', validCount, 'text-indigo-600'],
                ].map(([l, v, c]) => (
                  <div key={l} className="rounded-xl bg-slate-50 px-4 py-3">
                    <p className={`text-2xl font-bold tracking-tight ${c}`}>{v}</p>
                    <p className="text-xs text-slate-500">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-4">
                {[
                  ['Tenant', tenantLabel(tenant)],
                  ['Mənbə', `${src.file} › ${src.table}`],
                  ['Hədəf', tgt.label],
                  ['Sətir', `${validCount} / ${src.rows.length}`],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-medium text-slate-400">{l}</p>
                    <p className="truncate text-sm font-semibold text-slate-800">{v}</p>
                  </div>
                ))}
              </div>
              {src.rows.length > validCount && (
                <p className="mt-3 flex items-center gap-1.5 text-xs text-amber-700">
                  <TriangleAlert className="h-3.5 w-3.5" /> {src.rows.length - validCount} sətir «{reqField?.l}» boş olduğu üçün keçiləcək.
                </p>
              )}
              <div className="mt-5 overflow-hidden rounded-2xl ring-1 ring-slate-200/70">
                <div className="flex items-center justify-between bg-slate-50/80 px-4 py-2.5">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Eye className="h-3.5 w-3.5 text-slate-400" /> Önizləmə (ilk 10 sətir)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-400">
                        {tgt.fields.filter(fd => map[fd.f] || fd.opts).map(fd => <th key={fd.f} className="whitespace-nowrap px-4 py-2 font-semibold">{fd.l}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {mapped.filter(isValid).slice(0, 10).map((r, i) => (
                        <tr key={i} className="hover:bg-slate-50/60">
                          {tgt.fields.filter(fd => map[fd.f] || fd.opts).map(fd => {
                            const val = fd.opts ? (fd.opts.find(o => o.v === r[fd.f])?.t || r[fd.f]) : r[fd.f];
                            return (
                              <td key={fd.f} className="max-w-[220px] truncate whitespace-nowrap px-4 py-2 text-slate-700">
                                {cellStr(val).trim() ? cellStr(val) : <span className="text-slate-300">boş</span>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {prog !== null && (
                <div className="mt-5">
                  <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-600">
                    <span>Import olunur</span><span className="font-mono text-[#0090bb]">{prog}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#0090bb] to-indigo-500 transition-all" style={{ width: `${prog}%` }} />
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* ── Alt naviqasiya: Geri / Növbəti / Import ── */}
      <div className="sticky bottom-4 z-10">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/90 p-3 shadow-xl shadow-slate-900/10 backdrop-blur-xl">
          <button type="button" onClick={() => go(page - 1)} disabled={page === 1 || busy}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:invisible">
            <ArrowLeft className="h-4 w-4" /> Geri
          </button>
          <div className="min-w-0 flex-1 text-center text-xs">
            {hint
              ? <span className="inline-flex items-center gap-1.5 font-medium text-amber-700"><TriangleAlert className="h-3.5 w-3.5 flex-shrink-0" /> {hint}</span>
              : <span className="font-medium text-slate-400">Addım {page} / 4 · {steps[page - 1].t}</span>}
          </div>
          {page < 4 ? (
            <button type="button" onClick={() => go(page + 1)} disabled={!canNext}
              className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40">
              Növbəti <ArrowRight className="h-4 w-4" />
            </button>
          ) : result ? (
            <button type="button" onClick={() => { setResult(null); setPage(2); }}
              className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
              Başqa cədvəl <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={doImport} disabled={!ready || busy}
              className="inline-flex items-center gap-2 rounded-xl bg-[#0090bb] px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#007ea4] disabled:cursor-not-allowed disabled:opacity-40">
              {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {busy ? 'Import olunur' : `Import et (${validCount})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
